// OS interview questions data
export const osQuestions = {
  'basics': [
    {
      id: 1,
      question: "What is an Operating System?",
      answer: "An OS is the software between your apps and the hardware. It shares CPU, memory, disks, and devices fairly, and gives programs simple APIs instead of raw machine details. Windows, Linux, and macOS are examples."
    },
    {
      id: 2,
      question: "What are the main functions of an OS?",
      answer: "It manages processes (running programs), memory, files on disk, device I/O, and security (who can do what). Apps ask the OS for services instead of poking hardware directly."
    },
    {
      id: 3,
      question: "Difference between a process and a program.",
      answer: "A program is the file on disk (instructions and data waiting to run). A process is that program actually loaded and executing, with its own memory, registers, and OS bookkeeping. One program file can spawn many processes."
    },
    {
      id: 4,
      question: "What is a kernel?",
      answer: "The kernel is the trusted core of the OS that runs in privileged mode, schedules CPU, maps memory, drives devices, and handles interrupts. Shells and apps sit above it in user space."
    },
    {
      id: 5,
      question: "Difference between kernel mode and user mode.",
      answer: "Kernel mode can touch all hardware and kernel memory. User mode is restricted so a buggy app cannot crash the whole machine. Apps enter kernel mode only through controlled gates like system calls."
    },
    {
      id: 6,
      question: "What is a system call?",
      answer: "A system call is how user code asks the kernel for a privileged service: open a file, fork a process, send data on a socket. Each call switches from user mode to kernel mode and back. Examples: read, write, fork, exit."
    },
    {
      id: 7,
      question: "What is a shell?",
      answer: "A shell is the text interface (or script runner) that reads commands, finds programs, and asks the OS to run them. Bash, zsh, and PowerShell are common shells."
    },
    {
      id: 8,
      question: "What is a real-time operating system (RTOS)?",
      answer: "An RTOS is built so critical tasks meet strict deadlines (medical devices, car ECUs, industrial control). Missed deadlines are treated as failures, unlike a general desktop OS that favors average throughput."
    }
  ],

  'process-management': [
    {
      id: 1,
      question: "What are the different states of a process?",
      answer: "Typical states: New (being created), Ready (waiting for CPU), Running (on CPU), Waiting/Blocked (waiting on I/O or an event), Terminated (finished, cleanup pending). The OS moves processes between these states."
    },
    {
      id: 2,
      question: "What is a Process Control Block (PCB)?",
      answer: "The PCB is the kernel’s row for one process: PID, state, program counter, registers, memory limits, open files, etc. On a context switch the OS saves the old PCB and loads the next so the CPU can resume later."
    },
    {
      id: 3,
      question: "What is a context switch?",
      answer: "A context switch pauses one process, stores its CPU state, and loads another’s state so the core runs a different process. Useful work pauses briefly; too many switches hurt performance."
    },
    {
      id: 4,
      question: "What is a thread?",
      answer: "A thread is one execution path inside a process. Threads in the same process share address space and open files but each has its own stack and registers. Creating a thread is lighter than creating a whole new process."
    },
    {
      id: 5,
      question: "Difference between process and thread.",
      answer: "Processes are isolated by separate memory maps; one crash usually does not kill others. Threads share memory inside one process, so communication is easy but one bad thread can corrupt shared data or crash the whole process."
    },
    {
      id: 6,
      question: "What is a zombie process?",
      answer: "A zombie has exited but its slot stays in the process table until the parent reads its exit status with wait(). It uses almost no memory, yet it still holds a PID until reaped."
    },
    {
      id: 7,
      question: "What is an orphan process?",
      answer: "An orphan is a child whose parent exited first. On Unix the child is reparented to init (PID 1) or a similar reaper so wait() can still happen and zombies do not pile up forever."
    },
    {
      id: 8,
      question: "What is the fork() system call?",
      answer: "fork() clones the calling process. The parent gets the child’s PID as the return value; in the child, fork returns 0. Both copies continue from the same code point with copied (often copy-on-write) memory state."
    },
    {
      id: 9,
      question: "What is Inter-Process Communication (IPC)?",
      answer: "IPC lets separate processes exchange data: pipes, message queues, shared memory, sockets, etc. Shared memory is fastest for bulk data; pipes are simple for producer-consumer streams."
    }
  ],

  'cpu-scheduling': [
    {
      id: 1,
      question: "What is CPU scheduling?",
      answer: "CPU scheduling picks which ready process runs next on each core. Goals include high CPU use, fair wait times, and meeting priorities. The short-term scheduler makes this decision often, on every tick or syscall return."
    },
    {
      id: 2,
      question: "Difference between preemptive and non-preemptive scheduling.",
      answer: "Preemptive means the OS can stop a running process to give the CPU to another (time slice or higher priority arrival). Non-preemptive runs each job until it blocks or exits. Preemption improves responsiveness but adds switch overhead."
    },
    {
      id: 3,
      question: "Explain FCFS scheduling.",
      answer: "First-Come First-Served runs jobs in arrival order with no preemption. It is simple but the convoy effect lets one long job block many short ones behind it, raising average wait time."
    },
    {
      id: 4,
      question: "Explain SJF scheduling.",
      answer: "Shortest Job First prefers the job with smallest next CPU burst, which minimizes average waiting time in theory. Burst length is often unknown ahead of time; the preemptive variant is Shortest Remaining Time First (SRTF)."
    },
    {
      id: 5,
      question: "Explain Round Robin scheduling.",
      answer: "Round Robin gives each ready process a fixed time quantum in rotation. When time expires, the process goes to the back of the queue. Fair for interactive systems; quantum length trades responsiveness vs overhead."
    },
    {
      id: 6,
      question: "What is priority scheduling?",
      answer: "The ready job with highest priority runs first. It can be preemptive or not. Risk: low-priority tasks may starve unless you raise priority for jobs that wait too long (aging)."
    },
    {
      id: 7,
      question: "What is starvation and aging?",
      answer: "Starvation means a task never gets CPU because higher-priority work keeps arriving. Aging slowly increases the priority of long-waiting tasks so everyone eventually runs."
    },
    {
      id: 8,
      question: "What is the convoy effect?",
      answer: "In FCFS, one long CPU-bound job holds the core while many short jobs queue behind it, inflating average wait. Shorter-job or time-sliced schedulers reduce this pattern."
    }
  ],

  'memory-management': [
    {
      id: 1,
      question: "What is paging?",
      answer: "Paging cuts physical RAM and virtual address space into fixed-size pages and frames. A page table maps virtual pages to frame numbers. It removes external fragmentation between variable chunks but can waste a little space inside the last page of a small object (internal fragmentation)."
    },
    {
      id: 2,
      question: "What is segmentation?",
      answer: "Segmentation splits a program into variable-size logical pieces (code, stack, heap) each with base and limit. It matches programmer thinking but can leave unusable holes between segments (external fragmentation)."
    },
    {
      id: 3,
      question: "What is virtual memory?",
      answer: "Virtual memory lets each process see a large private address space while only active pages sit in RAM. Inactive pages live on disk (swap). That lets more programs run than physical RAM alone could hold."
    },
    {
      id: 4,
      question: "What is a page fault?",
      answer: "A page fault happens when the CPU accesses a valid virtual page that is not currently loaded in RAM. The OS loads it from disk (or zeros a new page), updates the page table, and retries the instruction."
    },
    {
      id: 5,
      question: "What is thrashing?",
      answer: "Thrashing is when the system spends most time swapping pages in and out instead of running instructions, usually because total working set size exceeds RAM. Fix: add RAM, kill or suspend jobs, or tune memory limits."
    },
    {
      id: 6,
      question: "Difference between internal and external fragmentation.",
      answer: "Internal fragmentation is wasted space inside an allocated unit (you asked for a page but only used part of it). External fragmentation is free memory split into small scattered holes that cannot satisfy a large contiguous request. Paging avoids external fragmentation between variable requests; compaction can help segmented heaps."
    },
    {
      id: 7,
      question: "What is the LRU page replacement algorithm?",
      answer: "LRU (Least Recently Used) evicts the page that has gone longest without a reference on a fault. It matches temporal locality: recent pages are likely needed again."
    },
    {
      id: 8,
      question: "What is the FIFO page replacement algorithm?",
      answer: "FIFO drops the oldest-loaded page on a fault. Simple queue, but Belady’s anomaly can appear: more frames sometimes increase faults. LRU avoids that oddity in common workloads."
    },
    {
      id: 9,
      question: "What is a TLB (Translation Lookaside Buffer)?",
      answer: "The TLB is a small, fast cache of recent virtual-to-physical translations. On a TLB hit the MMU avoids a full page-table walk in RAM, so normal memory access stays quick."
    },
    {
      id: 10,
      question: "What is demand paging?",
      answer: "Demand paging loads a page into RAM only on first access instead of loading the whole program at start. That speeds startup and saves RAM; it is how most virtual memory systems behave."
    }
  ],

  'synchronization': [
    {
      id: 1,
      question: "What is the critical section problem?",
      answer: "The critical section is code that uses shared data. You need mutual exclusion (only one thread there at a time), progress (someone enters if no one is inside), and bounded waiting (no thread waits forever)."
    },
    {
      id: 2,
      question: "What is a mutex?",
      answer: "A mutex is a lock: one thread holds it while in the critical section; others block until release. Usually only the owner should unlock it."
    },
    {
      id: 3,
      question: "What is a semaphore?",
      answer: "A semaphore holds a count updated atomically with wait (decrement, maybe block) and signal (increment, maybe wake). A binary semaphore behaves like a mutex; a counting semaphore tracks N identical resources."
    },
    {
      id: 4,
      question: "Difference between mutex and semaphore.",
      answer: "Mutex emphasizes ownership of one object. Semaphore can signal across threads without a single owner and can count N slots, not just locked/unlocked."
    },
    {
      id: 5,
      question: "What is a deadlock?",
      answer: "Deadlock is a cycle of processes each holding a resource and waiting for another’s resource, so none can move. Classic four conditions: mutual exclusion, hold-and-wait, no preemption, circular wait. Break any one to prevent many deadlocks."
    },
    {
      id: 6,
      question: "What are the four Coffman conditions for deadlock?",
      answer: "Mutual exclusion (non-shareable resource in use by one), hold and wait (keep one resource while asking for another), no preemption (kernel will not forcibly take the resource), circular wait (a ring of waits). All four together allow deadlock."
    },
    {
      id: 7,
      question: "What is the Banker's Algorithm?",
      answer: "A deadlock avoidance scheme for known max needs: before granting a resource request, the OS simulates whether any safe sequence still finishes all jobs. If the state would become unsafe, the request waits or is denied."
    },
    {
      id: 8,
      question: "What is a race condition?",
      answer: "A race happens when two threads read/write shared data without rules and the result depends on timing. Fix with mutexes, semaphores, atomic operations, or message passing."
    },
    {
      id: 9,
      question: "What is deadlock prevention vs deadlock avoidance?",
      answer: "Prevention removes a Coffman condition by design (for example a global lock order to kill circular wait). Avoidance uses runtime checks like Banker’s to stay in only safe states. Prevention is simpler but can waste resources; avoidance needs extra information."
    },
    {
      id: 10,
      question: "What is a monitor?",
      answer: "A monitor packages shared data with procedures and a lock so only one thread runs inside at a time, plus condition variables for wait/signal style waiting. Higher level and harder to misuse than raw locks scattered everywhere."
    }
  ],

  'file-systems': [
    {
      id: 1,
      question: "What is a file system?",
      answer: "The file system is how bits on a disk become named files, folders, permissions, and metadata. Examples: ext4 on Linux, NTFS on Windows, APFS on macOS."
    },
    {
      id: 2,
      question: "What is an inode?",
      answer: "On Unix-like systems an inode stores file metadata (size, owner, mode, timestamps, pointers to data blocks). Directory entries map human-readable names to inode numbers."
    },
    {
      id: 3,
      question: "Difference between hard link and soft link.",
      answer: "A hard link is another directory entry pointing to the same inode; data survives until the last link is removed. A symbolic (soft) link stores a path string; if the target file moves or deletes, the link can break."
    },
    {
      id: 4,
      question: "What are the different file allocation methods?",
      answer: "Contiguous stores a file in one run of blocks (fast sequential access, hard to grow). Linked chains blocks with pointers (no external holes, slow random seek). Indexed keeps a block of pointers (inode-style) for good random access without a full contiguous stretch."
    },
    {
      id: 5,
      question: "What is journaling in a file system?",
      answer: "Journaling writes intended metadata changes to a log on disk before applying them to the main structures. After a crash the OS replays or undoes partial updates so the tree stays consistent. ext4 and NTFS use journaling ideas."
    },
    {
      id: 6,
      question: "What is mounting in a file system?",
      answer: "Mounting attaches a volume’s file system at a directory path in the global tree so users see one unified namespace. Unmount flushes caches and detaches before unplugging removable media."
    }
  ]
};

// Helper function to get all questions from all OS topics
export const getAllOsQuestions = () => {
  const allQuestions = [];
  Object.keys(osQuestions).forEach(topic => {
    osQuestions[topic].forEach(q => {
      allQuestions.push({ ...q, topic });
    });
  });
  return allQuestions;
};

// Helper function to shuffle array
export const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};
